import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Checkbox, Button } from '@mui/material';
import { toast } from 'react-toastify';
import { Context } from '../../AuthContext';
import Card from '../../components/Card/Card';
import Spinner from '../../components/Spinner';
import { categories } from '../../utils/cardInfo';
import { formatReferences, STYLE_KEYS } from '../../components/References/cslFormatter';
import './AddReferencesFromResources.css';

// Page reached from the References "+" → "Add from Existing Vertical Resource(s)".
// Lists the vertical's publications (rendered with the shared Card component)
// with checkboxes; selected ones are turned into references (server builds the
// citation) and the user is sent back.
const AddReferencesFromResources = () => {
  const { vertical_id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(Context);

  const [papers, setPapers] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [style, setStyle] = useState('MLA');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!user?.uid || !vertical_id) return;
      try {
        const token = await user.getIdToken();
        const res = await fetch(
          `${process.env.REACT_APP_FLASK_WEBSERVER}get_papers?vertical_id=${vertical_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setPapers(data.data || []);
      } catch (error) {
        console.error('Failed to load publications:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.uid, vertical_id]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0 || !user) return;
    setSubmitting(true);
    try {
      // Selection order: a Set preserves insertion order.
      const selectedPapers = [...selected].map((id) => papers.find((p) => p._id === id));
      const references = await formatReferences(selectedPapers, style);

      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}add_references_bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ vertical_id, references }),
      });
      if (res.ok) {
        toast.success('Reference(s) added', { className: 'toast', autoClose: 3000 });
        navigate(-1);
      } else {
        toast.error('Failed to add references', { className: 'toast', autoClose: 3000 });
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Failed to add references:', error);
      toast.error('Failed to add references', { className: 'toast', autoClose: 3000 });
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="add-refs-page">
      <h2 className="add-refs-heading">Select the Resource(s) to Add as a Reference</h2>

      <div className="add-refs-style">
        <span className="add-refs-style-label">Reference Style:</span>
        <div className="add-refs-style-options">
          {STYLE_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className={`add-refs-style-btn${style === key ? ' add-refs-style-btn--active' : ''}`}
              onClick={() => setStyle(key)}
            >
              {key}
            </button>
          ))}
        </div>
      </div>

      <div className="add-refs-actions">
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={selected.size === 0 || submitting}
          sx={{ textTransform: 'none', minWidth: 320, paddingY: '10px', fontSize: '16px' }}
        >
          Add Reference(s)
        </Button>
      </div>

      <div className="add-refs-list">
        {papers.map((paper) => (
          <div key={paper._id} className="add-refs-row">
            <Checkbox
              checked={selected.has(paper._id)}
              onChange={() => toggle(paper._id)}
              className="add-refs-checkbox"
            />
            <div className="add-refs-card">
              <Card
                category="papers"
                details={paper}
                editor={false}
                cardHeader={categories.papers.cardHeader}
                icons={categories.papers.icons}
                dotColor={categories.papers.dotColor}
                vertical_id={vertical_id}
                uid={user.uid}
                visibleBookmark={false}
                openNewTab
              />
            </div>
          </div>
        ))}

        {papers.length === 0 && (
          <p className="add-refs-empty">This vertical has no publications to add.</p>
        )}
      </div>
    </div>
  );
};

export default AddReferencesFromResources;
